import React, { useState, useMemo } from 'react';
import { sharedTypographyStyles, FONT_SIZE_SECTION_TEXT, FONT_SIZE_SUB_TEXT } from './sharedStyles';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import BodyMapOverlay from './BodyMapOverlay';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Collapse,
  Grid,
  Typography,
  makeStyles,
  colors,
  Divider,
  CardHeader
} from '@material-ui/core';
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import FavoriteIcon from '@material-ui/icons/Favorite';
import WarningIcon from '@material-ui/icons/Warning';
import LinkIcon from '@material-ui/icons/Link';
import AssignmentIcon from '@material-ui/icons/Assignment';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    background: '#f7faff',
    borderRadius: 14,
    boxShadow: '0 6px 32px rgba(0,45,85,0.18)',
    padding: 0,
    marginTop: 0,
  },
  avatar: {
    backgroundColor: '#6a8caf', // soft blue
    height: 'clamp(20px, 2.2vw, 36px)',
    width: 'clamp(20px, 2.2vw, 36px)',
  },
  sectionCard: {
    background: 'linear-gradient(90deg, #dce4eb, #d7d4cf)',
    borderRadius: 16,
    boxShadow: '0 2px 12px rgba(106,140,175,0.07)',
    padding: 20,
    marginBottom: 18,
    marginTop: 8,
    width: '100%',
    maxWidth: 700,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  columnStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    marginTop: -10,
  },
  adviceWrap: {
    width: '100%',
  },
  drawerCard: {
    background: 'linear-gradient(90deg, #dce4eb, #d7d4cf)',
    borderRadius: 16,
    boxShadow: '0 2px 12px rgba(106,140,175,0.17)',
    padding: 16,
    margin: '8px 0 16px 0',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  ...sharedTypographyStyles,
  sectionHeader2: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 'normal',
    color: '#002D55',
    letterSpacing: 0.2,
    fontSize: 'clamp(14px, 1.3vw, 20px)',
  },
  icon: {
    marginRight: 10,
    color: '#6a8caf', // soft blue
    fontSize: 'clamp(18px, 1.4vw, 22px)',
  },
  drawerArrow: {
    marginLeft: 8,
    fontSize: 18,
    color: '#6a8caf',
    transition: 'transform 0.2s ease',
    display: 'inline-block',
  },
  summaryBox: {
    background: '#eaf4fb', // gentle blue
    borderRadius: 12,
    padding: 8,
    marginBottom: 12,
    border: '1.5px solid #b3d3ea',
    color: '#3a4a5d',
    fontWeight: 500,
    maxWidth: 700,
    position: 'relative',
    fontSize: 'clamp(0.8rem, 0.85vw, 1.04rem)',
  },
  bullet: {
    color: '#3a4a5d',
    fontWeight: 700,
    marginRight: 10,
    marginLeft: 2,
    fontSize: 'clamp(0.88rem, 0.9vw, 1.1rem)',
  },

  divider: {
    margin: '18px 0',
    width: '100%',
    background: '#e3eaf1',
  },
  journalGroupTitle: {
    fontWeight: 700,
    color: '#002D55',
    fontSize: FONT_SIZE_SECTION_TEXT,
    marginBottom: 4,
    marginTop: 2,
  },
  journalDrawerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    userSelect: 'none',
    padding: 'clamp(10px, 0.7vw, 12px) clamp(12px, 1.05vw, 16px)',
    minHeight: 'clamp(38px, calc(20px + 1.3vw), 48px)',
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #dce9f4',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#e8f0f9',
    },
  },
  journalDrawerContent: {
    background: 'linear-gradient(90deg, #dce4eb, #d7d4cf)',
    borderRadius: '0 0 14px 14px',
    boxShadow: '0 2px 12px rgba(106,140,175,0.07)',
    padding: '12px 16px',
    marginTop: 0,
    color: '#002D55',
    lineHeight: 1.6,
    fontSize: FONT_SIZE_SUB_TEXT,
  },
  journalDrawerArrow: {
    marginLeft: 'clamp(8px, 0.7vw, 12px)',
    fontSize: 'clamp(14px, 1.15vw, 18px)',
    transition: 'transform 0.2s ease',
    display: 'inline-block',
  },
  journalItem: {
    color: '#002D55',
    lineHeight: 1.6,
    fontSize: FONT_SIZE_SUB_TEXT,
    marginBottom: 2,
  },
  journalNumeric: {
    fontWeight: 600,
    color: '#002D55',
    fontSize: FONT_SIZE_SUB_TEXT,
  },
  tabButtons: {
    display: 'flex',
    gap: 0,
    marginBottom: 8,
    borderBottom: '2px solid #e0e0e0',
    width: '100%',
  },
  tabButton: {
    padding: '10px 16px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'Bold',
    color: '#002D55',
    fontSize: 'clamp(14px, 1.3vw, 21px)',
    letterSpacing: 0.2,
    transition: 'all 0.2s ease',
    borderBottom: '3px solid transparent',
    marginBottom: '-2px',
  },
}));

// Journal grouping — mirrors SelectedPatientDiagnosis.js grouping for patient-facing display
const JOURNAL_GROUPING = {
  'Your primary complaint': [
    'keySymptom', 'painAnamnesis', 'painAnamnesis_cause_how_issue_occured',
    'painAnamnesis_duration', 'painAnamnesis_doctor', 'painAnamnesis_doctorDiagnosis',
    'secondary_multimorbid_doctorDiagnosis', 'painAnamnesis_episodes',
  ],
  'Location and character of symptoms': [
    'painAnamnesis_location', 'painAnamnesis_knownCondition', 'painAnamnesis_painCharacteristics',
  ],
  'Activities, pattern and development': [
    'painAnamnesis_pattern', 'painAnamnesis_affectedActivities',
    'painAnamnesis_development',
  ],
  'Pain baseline levels': [
    'painAnamnesis_frequency', 'painAnamnesis_painAtBest', 'painAnamnesis_painCurrent',
    'painAnamnesis_painAtWorst', 'painAnamnesis_painAtRest', 'painAnamnesis_painWithActivity',
    'painAnamnesis_difficulty', 'painAnamnesis_function',
  ],
  'Your goal for treatment': [
    'painAnamnesis_SelfRatedFunction_MainGoal',
  ],
  'Red flags': [
    'redflags',
  ],
  'Movement tests': [
    'movement_test',
  ],
  'Symptom response on clinical tests': [
    'clinicalTest', 'SymptomProvoking_clinicalTest',
    'SymptomImproving_clinicalTest', 'keyFunctionalLimitations',
  ],
};

const JOURNAL_ORDERED_KEYS = [
  'keySymptom',
  'painAnamnesis',
  'painAnamnesis_location',
  'painAnamnesis_painCharacteristics',
  'redflags',
  'painAnamnesis_pattern',
  'painAnamnesis_affectedActivities',
  'painAnamnesis_cause_how_issue_occured',
  'painAnamnesis_duration',
  'painAnamnesis_frequency',
  'painAnamnesis_painAtBest',
  'painAnamnesis_painCurrent',
  'painAnamnesis_painAtWorst',
  'painAnamnesis_painWithActivity',
  'painAnamnesis_painAtRest',
  'painAnamnesis_difficulty',
  'painAnamnesis_aggravatingFactors',
  'painAnamnesis_relievingFactors',
  'painAnamnesis_doctor',
  'painAnamnesis_doctorDiagnosis',
  'secondary_multimorbid_doctorDiagnosis',
  'painAnamnesis_development',
  'painAnamnesis_episodes',
  'painAnamnesis_function',
  'painAnamnesis_knownCondition',
  'movement_test',
  'clinicalTest',
  'SymptomProvoking_clinicalTest',
  'SymptomImproving_clinicalTest',
  'keyFunctionalLimitations',
  'painAnamnesis_SelfRatedFunction_MainGoal',
];

const findMatchedAnswer = (item) => {
  if (!item) return null;
  const sel = item.selected_answer || item.selected_answer_meta || {};
  const candidates = [];
  const addIf = (value) => {
    if (value !== undefined && value !== null && value !== '') {
      candidates.push(String(value));
    }
  };

  addIf(sel.answer_id);
  addIf(sel.answerId);
  addIf(sel.value);
  addIf(sel.label);
  addIf(sel.text);
  addIf(sel.answerText);
  addIf(sel.answer_text);
  addIf(sel.canonical);
  addIf(sel.text_value);
  addIf(sel.numeric_value);
  addIf(item.answer_id);
  addIf(item.answerId);
  addIf(item.answer_text);
  addIf(item.answerText);
  if (item.selected_answer_meta && item.selected_answer_meta.value) {
    addIf(item.selected_answer_meta.value);
  }

  const answers = item.question && Array.isArray(item.question.answers) ? item.question.answers : [];
  if (!answers.length) return null;

  for (const answer of answers) {
    if (!answer) continue;
    const answerValues = [];
    const pushAnswerValue = (value) => {
      if (value !== undefined && value !== null) {
        answerValues.push(String(value));
      }
    };
    pushAnswerValue(answer.value);
    pushAnswerValue(answer.id);
    pushAnswerValue(answer.answerId);
    pushAnswerValue(answer.label);
    pushAnswerValue(answer.text);
    pushAnswerValue(answer.answerText);
    pushAnswerValue(answer.answer_text);

    for (const candidate of candidates) {
      if (answerValues.includes(String(candidate))) {
        return answer;
      }
    }
  }

  return null;
};

const getItemTags = (item) => {
  const matchedAnswer = findMatchedAnswer(item);
  if (matchedAnswer && Array.isArray(matchedAnswer.tags) && matchedAnswer.tags.length) {
    return matchedAnswer.tags;
  }
  if (item.selected_answer_meta && Array.isArray(item.selected_answer_meta.tags) && item.selected_answer_meta.tags.length) {
    return item.selected_answer_meta.tags;
  }
  if (item.question && Array.isArray(item.question.tags) && item.question.tags.length) {
    return item.question.tags;
  }
  if (Array.isArray(item.tags) && item.tags.length) {
    return item.tags;
  }
  return [];
};

// Resolve display text from a journal item
const getItemDisplayText = (item) => {
  const cleanDisplayText = (value) => {
    if (value === undefined || value === null) return '';
    let text = String(value).trim();
    if (!text) return '';
    if (/^(?:answer|ans)[_-]?\d+$/i.test(text) || /^[a-z]$/i.test(text)) return '';
    text = text.replace(/\b(?:answer[_-]?\d+|ans[_-]?\d+)\b/gi, '');
    text = text.replace(/(^|\r?\n)\s*[A-Za-z]\s*(?=\r?\n|$)/g, '$1');
    text = text.replace(/\r?\n\s*\r?\n+/g, '\n');
    text = text.replace(/[ \t]{2,}/g, ' ').trim();
    return text;
  };
  const stripGoalPrefix = (value, labels = []) => {
    let text = cleanDisplayText(value);
    if (!text) return '';
    labels.filter(Boolean).forEach((label) => {
      const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      text = text.replace(new RegExp(`^${escaped}[\\s:–—-]*`, 'i'), '').trim();
    });
    return text;
  };
  const isGoalItem = [item.mapped_key, item.identifier, item.subgroup, item.theme, item.kpi_type]
    .filter(Boolean)
    .some((value) => {
      const lowered = String(value).toLowerCase();
      return lowered === 'function_goal'
        || lowered.includes('painanamnesis_selfratedfunction_maingoal')
        || lowered.includes('psfs_selfratedfunction_maingoal')
        || lowered.includes('selfratedfunction_maingoal');
    });
  const isFrequencyItem = [item.mapped_key, item.identifier, item.subgroup, item.theme, item.kpi_type]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes('frequency'));
  const redflagIdentifierBlob = [
    item && item.identifier,
    item && item.subgroup,
    item && item.theme,
    item && item.mapped_key,
    item && item.question && item.question.identifier,
    item && item.question_id,
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase())
    .join(' ');
  const redflagLabel = redflagIdentifierBlob.includes('accompaniedbysystemicsymptoms')
    ? 'Accompanying systemic symptoms'
    : ((redflagIdentifierBlob.includes('redflags_1') || redflagIdentifierBlob.includes('neurological redflag'))
      ? 'Patient reported neurological redflag symptoms'
      : null);
  // Prefer journalPhrase from selected_answer_meta, then item-level fields
  const meta = item.selected_answer_meta;
  const sanitize = (s) => s.replace(/and baseline scored it at\s*:?\s*/gi, '').trim();
  const journal = cleanDisplayText(sanitize((meta && meta.journalPhrase) || item.journalPhrase || item.journal_phrase || item.journal_text || ''));
  const metaLabel = cleanDisplayText(sanitize((meta && meta.metaJournalPhrase) || item.metaJournalPhrase || item.meta_journalPhrase || ''));
  const answerText = cleanDisplayText(item.answer_text || (meta && (meta.label || meta.answerText)) || '');
  const numericValue = (item.numeric_value !== undefined && item.numeric_value !== null && item.numeric_value !== '')
    ? item.numeric_value
    : ((item.numerical_value !== undefined && item.numerical_value !== null && item.numerical_value !== '') ? item.numerical_value : null);
  // For numeric items (pain scales), show as "Label: answerText — value"
  if (numericValue != null && numericValue !== '') {
    if (isGoalItem) {
      const label = (metaLabel || 'Patient described functional goal (PSFS)').replace(/:?\s*$/, '');
      const normalizeGoalText = (value) => String(value || '')
        .replace(/[\s:–—-]*\d+\s*(?:\/\s*100)?\s*$/i, '')
        .trim();
      const rawGoalText = stripGoalPrefix(journal, [metaLabel, 'Patient described functional goal (PSFS):', 'The patient described their main functional goal (PSFS) as:']) || answerText;
      const goalText = normalizeGoalText(rawGoalText);
      return goalText ? `${label}: ${goalText} — ${numericValue} / 100` : `${label}: ${numericValue} / 100`;
    }
    if (isFrequencyItem) {
      return `Patient reported frequency of symptoms: — ${numericValue} / 7 days`;
    }
    const label = metaLabel || journal || item.identifier || '';
    const parts = [];
    if (label) parts.push(label.replace(/:?\s*$/, ''));
    if (answerText && answerText.trim() && answerText.trim().toLowerCase() !== String(label || '').trim().toLowerCase()) parts.push(answerText.trim());
    const prefix = parts.join(' — ');
    return prefix ? `${prefix}: ${numericValue}` : '';
  }
  if (isFrequencyItem) {
    const raw = `${answerText} ${journal}`.trim();
    const m = raw.match(/(\d+)\s*\/\s*7/i);
    if (m) return `Patient reported frequency of symptoms: — ${m[1]} / 7 days`;
  }
  if (redflagLabel) {
    const rawRedflagText = cleanDisplayText((journal || answerText || metaLabel || '').trim());
    const valueOnly = rawRedflagText
      .replace(/accompanying systemic symptoms:\s*/ig, '')
      .replace(/patient reported neurological redflag symptoms:\s*/ig, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
    const value = valueOnly || 'None.';
    return `${redflagLabel}: ${value}`;
  }
  // For text items, prefer journal phrase
  if (journal && journal.trim()) return journal.trim();
  // Fallback to metaLabel + answerText
  if (metaLabel && answerText) return `${metaLabel.replace(/:?\s*$/, '')}: ${answerText}`;
  if (answerText) return answerText;
  if (metaLabel) return metaLabel;
  return '';
};

const Prognosis = (props) => {
  const classes = useStyles();
  const className = props.className;
  const [showSummaryBox, setShowSummaryBox] = useState(true);
  const [openJournalDrawer, setOpenJournalDrawer] = useState(0);

  // Process journalSections into grouped display data (same grouping as admin journal view)
  const journalGroups = useMemo(() => {
    if (!props.journalSections) return [];
    const sections = props.journalSections;
    const consumed = new Set();
    const groups = [];

    const getItemId = (item) => item.question_id || item.id || JSON.stringify(item);

    const getTagPriority = (item) => {
      const tags = getItemTags(item);
      if (!Array.isArray(tags) || !tags.length) return JOURNAL_ORDERED_KEYS.length;
      for (let index = 0; index < JOURNAL_ORDERED_KEYS.length; index += 1) {
        if (tags.includes(JOURNAL_ORDERED_KEYS[index])) return index;
      }
      return JOURNAL_ORDERED_KEYS.length;
    };

    const sortItems = (items) => items.sort((a, b) => {
      const aPriority = getTagPriority(a);
      const bPriority = getTagPriority(b);
      if (aPriority !== bPriority) return aPriority - bPriority;

      const aIsManual = (a.manual === true)
        || (a.id && String(a.id).startsWith('manual-'))
        || (a.theme && String(a.theme).endsWith('_manual'));
      const bIsManual = (b.manual === true)
        || (b.id && String(b.id).startsWith('manual-'))
        || (b.theme && String(b.theme).endsWith('_manual'));
      if (aIsManual && !bIsManual) return 1;
      if (!aIsManual && bIsManual) return -1;

      const aAnswer = findMatchedAnswer(a);
      const bAnswer = findMatchedAnswer(b);
      const aSubgroup = ((aAnswer && (aAnswer.subgroup || aAnswer.type)) || a.subgroup || (a.question && a.question.subgroup) || '').toString();
      const bSubgroup = ((bAnswer && (bAnswer.subgroup || bAnswer.type)) || b.subgroup || (b.question && b.question.subgroup) || '').toString();
      const subgroupWeight = (value) => {
        if (!value) return 0;
        if (value === 'manual') return 2;
        if (value === 'default') return 3;
        return 0;
      };

      const aWeight = subgroupWeight(aSubgroup);
      const bWeight = subgroupWeight(bSubgroup);
      if (aWeight !== bWeight) return aWeight - bWeight;
      return aSubgroup.localeCompare(bSubgroup);
    });

    // Helper: collect all items from sections matching the given theme keys
    const pickItems = (keys) => {
      const items = [];
      const seenAnswerTexts = new Set();
      const dedupeByAnswerText = keys.includes('painAnamnesis_SelfRatedFunction_MainGoal');

      keys.forEach((key) => {
        const matchesForKey = [];

        Object.entries(sections).forEach(([theme, subgroups]) => {
          Object.entries(subgroups || {}).forEach(([subgroup, arr]) => {
            if (!Array.isArray(arr)) return;

            arr.forEach((item) => {
              const id = getItemId(item);
              if (consumed.has(id)) return;

              const questionIdentifier = (item.identifier || (item.question && item.question.identifier) || '').toString();
              const identifierMatch = questionIdentifier === key;
              const matchedAnswer = findMatchedAnswer(item);
              const answerSubgroup = (matchedAnswer && (matchedAnswer.subgroup || matchedAnswer.type)) || null;
              const canonicalTheme = typeof theme === 'string' ? theme.replace(/_manual$/, '') : theme;
              const combinedKey = `${canonicalTheme}_${subgroup}`;
              const combinedKeyForItem = item.subgroup ? `${canonicalTheme}_${item.subgroup}` : null;
              const subgroupMatch = (item.subgroup === key)
                || (answerSubgroup === key)
                || ((item.question && item.question.subgroup) === key)
                || (combinedKey === key)
                || (item.subgroup && `${canonicalTheme}_${item.subgroup}` === key);
              const themeMatch = (theme === key || canonicalTheme === key)
                && !(item.subgroup && item.subgroup !== 'default' && combinedKeyForItem && JOURNAL_ORDERED_KEYS.includes(combinedKeyForItem));
              const tags = getItemTags(item);
              const tagMatch = Array.isArray(tags) && tags.includes(key);

              let derivedMatch = false;
              const questionText = (((item.question && item.question.question) || '')).toString().toLowerCase();
              const questionId = questionIdentifier.toLowerCase();
              if (key === 'painAnamnesis_doctor') {
                if (questionId.includes('doctor') || questionText.includes('doctor') || questionText.includes('examined')) {
                  derivedMatch = true;
                }
              }
              if (key === 'painAnamnesis_doctorDiagnosis') {
                if (questionId.includes('diagnos') || questionText.includes('diagnos') || questionText.includes('diagnose')) {
                  derivedMatch = true;
                }
              }

              if (identifierMatch || subgroupMatch || themeMatch || tagMatch || derivedMatch) {
                matchesForKey.push(item);
              }
            });
          });
        });

        sortItems(matchesForKey).forEach((item) => {
          const id = getItemId(item);
          if (consumed.has(id)) return;
          consumed.add(id);

          const text = getItemDisplayText(item);
          if (!text) return;

          const answerText = (item.answer_text || '').trim().toLowerCase();
          if (dedupeByAnswerText && answerText && seenAnswerTexts.has(answerText)) return;
          if (dedupeByAnswerText && answerText) seenAnswerTexts.add(answerText);

          items.push({ id, text, numeric: item.numeric_value });
        });
      });

      return items;
    };

    Object.entries(JOURNAL_GROUPING).forEach(([groupName, keys]) => {
      const items = pickItems(keys);
      if (items.length > 0) {
        groups.push({ name: groupName, items });
      }
    });

    return groups;
  }, [props.journalSections]);

  const hasJournalData = journalGroups.length > 0;

  return (
    <Card className={clsx(classes.root, className)} style={{ background: '#f7faff' }}>
      <CardHeader
        avatar={<Avatar className={classes.avatar}><NoteAddIcon style={{ fontSize: 'clamp(14px, 1.4vw, 22px)' }} /></Avatar>}
        title={<span className={classes.cardHeaderTitle}>Learn About Your Symptoms</span>}
        style={{ marginBottom: 5, padding: 10, borderBottom: "2px solid #fb741a", alignItems: 'center' }}
      />
      <CardContent>
        <Grid container spacing={2} style={{marginLeft: 0, marginTop: 0, maxWidth: 1200}}>
          {/* Body Map */}
          {props.diagnosisIdentifier && (
            <Grid item xs={11} sm={5} md={4} lg={4} xl={5} style={{ display: 'flex' }} className="bodymap-grid-item">
              <BodyMapOverlay
                diagnosisIdentifier={props.diagnosisIdentifier}
                symptomLocations={props.symptomLocations || {}}
              />
              <style>{`
                @media (max-width: 600px) {
                  .bodymap-grid-item > div {
                    max-height: 40vh;
                  }
                  .bodymap-grid-item > div img {
                    max-height: 35vh;
                    object-fit: contain;
                  }
                }
              `}</style>
            </Grid>
          )}
          {/* Diagnosis, Recommendations, and Symptoms Worsen Info */}
          <Grid item xs={11} sm={7} md={props.diagnosisIdentifier ? 8 : 12} lg={props.diagnosisIdentifier ? 8 : 12} xl={props.diagnosisIdentifier ? 7 : 12} style={{paddingRight: 40}} className="prognosis-content-item">
            <style>{`
              @media (max-width: 600px) {
                .prognosis-content-item {
                  padding-left: 16px !important;
                  padding-right: 16px !important;
                }
              }
            `}</style>
            {showSummaryBox && (
              <Box className={classes.summaryBox}>
                <span role="img" aria-label="info" style={{ fontSize: 22, marginRight: 10 }}>💡</span>
                <span className={classes.subText} style={{ marginRight: 10 }}>
                  This page gives you a clear overview of your condition and our recommendations. <br /> If anything is unclear, you can always ask your assigned physiotherapist for more details.
                </span>
                <IconButton size="small" style={{ position: 'absolute', top: 4, right: 4, zIndex: 2 }} onClick={() => setShowSummaryBox(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {/* Your Assessment Overview — before diagnosis */}
            {hasJournalData && (
              <>
                <div className={classes.sectionHeader}><AssignmentIcon className={classes.icon}/> Your Assessment Overview</div>
                <Box style={{ marginTop: 8, marginBottom: 4, maxWidth: 700 }}>
                  {journalGroups.map((group, gIdx) => {
                    const isOpen = openJournalDrawer === gIdx;
                    return (
                      <Box key={gIdx} style={{ marginBottom: 10 }}>
                        <div
                          className={classes.journalDrawerRow}
                          style={{ borderRadius: isOpen ? '14px 14px 0 0' : 14 }}
                          role="button"
                          tabIndex={0}
                          onClick={() => setOpenJournalDrawer(isOpen ? null : gIdx)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setOpenJournalDrawer(isOpen ? null : gIdx);
                            }
                          }}
                        >
                          <Typography style={{ color: '#002D55', fontWeight: 600, fontSize: FONT_SIZE_SECTION_TEXT, margin: 0, lineHeight: 1.2 }}>
                            {group.name}
                          </Typography>
                          <span className={classes.journalDrawerArrow} style={{ color: isOpen ? '#FF7F27' : '#002D55', transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
                            {isOpen ? '▼' : '▶'}
                          </span>
                        </div>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box className={classes.journalDrawerContent}>
                            <ul style={{ paddingLeft: 20, margin: '4px 0 0 0' }}>
                              {group.items.map((item, iIdx) => (
                                <li key={item.id || iIdx} className={classes.journalItem}>
                                  {item.text}
                                </li>
                              ))}
                            </ul>
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
                <Divider className={classes.divider}/>
              </>
            )}

            <Typography className={classes.sectionHeader2}>
              <FavoriteIcon className={classes.icon}/> 
              <b>Suggested diagnosis: </b>{'\u00A0'}{props.title}
            </Typography>
            <Typography className={classes.subText}>
              {props.explainer}
            </Typography>
            <Divider className={classes.divider}/>
          
            <div className={classes.sectionHeader}><EmojiObjectsIcon className={classes.icon}/> Our Recommendations</div>
            <Box className={classes.sectionCard}>
              {props.indicationForPhysiotherapy && props.indicationForPhysiotherapy.trim() && props.indicationForPhysiotherapy.trim() !== 'N/A' ? (
                <>
                  <Typography variant="h6" style={{ fontWeight: 700, color: '#002D55', marginBottom: 6, letterSpacing: 0.2, fontSize: '1rem' }}>
                    Your symptoms are likely to benefit from physiotherapy.
                  </Typography>
                  <Typography style={{ color: '#002D55', fontSize: FONT_SIZE_SUB_TEXT, lineHeight: 1.7, marginBottom: 10 }}>
                    {props.indicationForPhysiotherapy}
                  </Typography>
                </>
              ) : (
                <Typography variant="h6" style={{ fontWeight: 700, color: '#002D55', marginBottom: 10, letterSpacing: 0.2, fontSize: '1rem' }}>
                  What you should do next:
                </Typography>
              )}
            </Box>
            <div className={classes.sectionHeader}><WarningIcon className={classes.icon}/> If Your Symptoms Worsen</div>
            <Typography className={classes.subText} style={{ marginTop: 8, marginBottom: 12, paddingRight: 35 }}>
              {props.If_your_symptoms_worsens}
            </Typography>
            <Divider className={classes.divider}/>

            {/* Reference Links */}
            {props.references && props.references.length > 0 && (
              <>
                <div className={classes.sectionHeader}><LinkIcon className={classes.icon}/> Further Reading About This Condition</div>
                <ul style={{ paddingLeft: 24, margin: '8px 0 12px 0' }}>
                  {props.references.map((ref, idx) => (
                    <li key={idx} style={{ marginBottom: 6 }}>
                      <a
                        href={ref.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.subText}
                        style={{ color: '#1a73e8', textDecoration: 'underline' }}
                      >
                        {ref.url}
                      </a>
                    </li>
                  ))}
                </ul>
                <Divider className={classes.divider}/>
                <Typography className={classes.subText} style={{ color: '#6a7a8a', fontStyle: 'italic', marginTop: -8, marginBottom: 8, fontSize: 'clamp(0.72rem, 0.78vw, 0.88rem)' }}>
                  <strong>Disclaimer:</strong> The links above contain general health information from third-party sources and are provided for educational purposes only. This information is not personalized to your specific condition, medical history, or treatment plan, and may not be applicable to your situation. Always follow the guidance of your assigned physiotherapist or healthcare provider.
                </Typography>
              </>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

Prognosis.propTypes = {
  className: PropTypes.string,
  treatmentId: PropTypes.string,
  diagnosisIdentifier: PropTypes.string,
  symptomLocations: PropTypes.object,
  journalSections: PropTypes.object,
  indicationForPhysiotherapy: PropTypes.string,
};

export default Prognosis;